import React, {PureComponent} from "react";
import {range} from "../../shared/utils/range";
import {cssHelper} from "../../shared/utils/cssHelper";
import {SceneryType} from "../../store/settings/SceneryType";
import styles from "./level-selection.module.css";
import icons from "../../shared/styles/icons.module.css";
import {random, Random, randomInt} from "../../shared/utils/random";
import {ILevelMapStats} from "../../store/game/types";
import {TweenLite, Elastic, Animation} from "gsap";
import {spread} from "../../shared/utils/spread";
import {AnimationSettingsProp} from "../../store/settings/types";
import numDigits from "../../shared/utils/numDigits";
import playSound from "../../shared/sounds/playSound";

interface LevelScreenProps {
    currentLevel: number;
    screenIndex: number;
    levelButtonsPerScreen: number;
    scenery: SceneryType;
    random: Random;
    levelStats: Record<number, ILevelMapStats>;
    currentLevelButtonRef: React.RefObject<HTMLButtonElement>;
    onLevelSelected?: (level: number) => void;
}

export class LevelScreen extends PureComponent<LevelScreenProps & AnimationSettingsProp> {
    private activeTweens: Animation[] = [];

    render() {
        const levelButtons = this.renderLevelButtons();
        const decals = this.renderDecals();

        const screenClassName = cssHelper()
            .add(styles.screen)
            .add(styles.screen_cut_flip, this.props.random.next() > 0.5)
            .add(styles.screen_bridge_flip, this.props.random.next() > 0.5)
            .add(`${this.props.scenery}_bg`);

        const pathClassName = cssHelper()
            .add(styles.path)
            .add(styles.path_flip, this.props.random.next() > 0.5)
            .add(`${this.props.scenery}_path`);

        return (<div className={screenClassName.toString()}>
            <div className={pathClassName.toString()}>
                {levelButtons}
                {decals}
            </div>
        </div>);
    }

    componentWillUnmount(): void {
        for (let animation of this.activeTweens) {
            animation.kill();
        }
    }

    private renderLevelButtons() {
        const {currentLevel, levelButtonsPerScreen, } = this.props;

        return range(levelButtonsPerScreen, this.props.screenIndex * levelButtonsPerScreen + 1).map((level, index) => {
            const levelStats = this.props.levelStats[level];
            const isCurrentLevel = level === currentLevel;
            const isLevelCompleted = levelStats && levelStats.completeLevelTime > 0 && !isCurrentLevel;
            const onClick = this.getOnLevelClickHandler(level, isCurrentLevel);

            const className = cssHelper()
                .add(styles.level)
                .add(styles[`level_${index + 1}`])
                .add(styles.level_locked, level > currentLevel)
                .add(styles.level_completed, isLevelCompleted)
                .add(styles.level_current, isCurrentLevel)
                .add(styles.blink, isCurrentLevel && this.props.animation.enabled && this.props.animation.timeScale > 0)
                .add(`${this.props.scenery}_level_${isLevelCompleted ? "completed_" : ""}button`);

            const stars = isLevelCompleted
                ? range(3).map((i) => <span key={i} className={cssHelper(styles.level_star, styles[`level_star_${i + 1}`], icons.score_star1).add(styles.star_disabled, (i + 1) > levelStats.stars).toString()}/>)
                : undefined;

            const animationDuration = this.props.animation.timeScale > 0
                ? (2.5 / this.props.animation.timeScale) + 's'
                : undefined;

            return (<button key={level} className={className.toString()} style={{ fontSize: 78 - numDigits(level) * 3, animationDuration }} onClick={onClick} ref={isCurrentLevel ? this.props.currentLevelButtonRef : undefined}>
                <span className={styles.level_number}>{level}</span>

                {stars}
            </button>);
        });
    }

    private renderDecals() {
        const sideDecals = range(2).map(index => {
            if (this.props.random.next() > 0.7)
                return null;

            const className = cssHelper()
                .add(styles.side_decal)
                .add(styles[`side_decal_${index + 1}`])
                .add(styles[`side_decal_size_${this.props.random.nextInt(1, 4)}`])
                .add(`${this.props.scenery}_side_decal_${this.props.random.nextInt(100) % 8 + 1}`);

            return (<div key={index} className={className.toString()}/>);
        });

        const decals = range(6).map(index => {
            if (this.props.random.next() > 0.85)
                return null;

            const size = this.props.random.equiprobable("big", "small");
            const className = cssHelper()
                .add(styles.decal)
                .add(styles[`${size}_decal`])
                .add(styles[`decal_${index + 1}`])
                .add(`${this.props.scenery}_${size}_decal_${this.props.random.nextInt(100) % 3 + 1}`);
            const style = {
                transform: `translateX(-50%) translateY(-50%) scaleX(${Math.random() > 0.5 ? 1 : -1}) scale(${random(0.85, 1).toFixed(1)}) rotate(${randomInt(-7, 7)}deg)`
            };

            return (<div key={index} className={className.toString()} style={style} onClick={(e) => this.shake(e.target as HTMLElement)}/>);
        });

        return (
            <>
                {sideDecals}
                {decals}
            </>
        );
    }

    private getOnLevelClickHandler(level: number, isCurrentLevel: boolean) {
       return () => {
           if (isCurrentLevel && this.props.onLevelSelected) {
               playSound("button-click");
               this.props.onLevelSelected(level);
           } else {
               playSound("unavailable-level-item-click");
           }
       };
    }

    private shake(target: HTMLElement) {
        if (!target)
            return;

        playSound("mouth-pop");

        const tween = TweenLite.fromTo(target, 0.33, {
            scale: 1,
            rotation: 1,
        }, {
            scale: spread([0.85, 1.1], 0.1),
            rotation: Math.round(spread([-7, 7], 0.2)),
            ease: Elastic.easeInOut.config(10, 0.3)
        });

        this.activeTweens.push(tween);
    }
}

